import { useCallback, useEffect, useRef, useState } from "react";
import { isSTTSupported, isTTSSupported, pickSpanishVoice, startListening as startSTT, unlockAudio } from "../lib/voice";

/**
 * Real-time voice pipeline hook (Fase 4b).
 *
 * Wraps the Web Speech API (SpeechRecognition + speechSynthesis) with:
 *   - Web Audio AnalyserNode for waveform visualization + level RMS
 *   - VAD (voice activity detection) via silence timer to auto-finalize dictation
 *   - Barge-in: if the user speaks while ONDI speaks, TTS is cancelled and
 *     listening resumes immediately.
 *
 * State machine: idle -> listening -> thinking -> speaking -> (auto) listening
 * The parent component drives higher-level "intents" via the exposed callbacks.
 *
 * SAFETY:
 * - No audio is uploaded or streamed to other users. The microphone is used
 *   ONLY for local voice-command recognition.
 * - All resources (stream, AudioContext, RAF loop, recognizer, TTS) are cleaned
 *   up when the hook is torn down.
 */

const BARGE_IN_THRESHOLD = 0.055;
const BARGE_IN_HOLD_MS = 220;
const VAD_SILENCE_MS = 1500;

export function useVoicePipeline({
  lang = "es-MX",
  onFinalTranscript,
  onError,
  bargeInEnabled = true,
  active = true,
} = {}) {
  const [state, setState] = useState("idle"); // idle|listening|thinking|speaking
  const [transcript, setTranscript] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [micError, setMicError] = useState(null);
  const supported = {
    stt: isSTTSupported(),
    tts: isTTSSupported(),
  };

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const stopSTTRef = useRef(null);
  const currentUtterRef = useRef(null);
  const bargeInStartRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const lastInterimRef = useRef("");
  const finalizedRef = useRef(false);
  const stateRef = useRef("idle");
  const aliveRef = useRef(true);
  const cbRef = useRef({ onFinalTranscript, onError });

  useEffect(() => {
    cbRef.current = { onFinalTranscript, onError };
  }, [onFinalTranscript, onError]);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const cancelSpeaking = useCallback(() => {
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch (_e) {
      /* ignore */
    }
    if (stateRef.current === "speaking") setState("idle");
  }, []);

  // Forward declaration via ref so callbacks can invoke each other freely
  const startListeningRef = useRef(() => {});

  const startAudioLoop = useCallback(() => {
    if (rafRef.current) return;
    const tick = () => {
      if (!aliveRef.current || !analyserRef.current) {
        rafRef.current = null;
        return;
      }
      const analyser = analyserRef.current;
      const data = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      setAudioLevel(rms);

      if (
        bargeInEnabled &&
        stateRef.current === "speaking" &&
        rms > BARGE_IN_THRESHOLD
      ) {
        if (!bargeInStartRef.current) bargeInStartRef.current = performance.now();
        else if (performance.now() - bargeInStartRef.current > BARGE_IN_HOLD_MS) {
          bargeInStartRef.current = null;
          cancelSpeaking();
          startListeningRef.current?.();
        }
      } else {
        bargeInStartRef.current = null;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [bargeInEnabled, cancelSpeaking]);

  const initMic = useCallback(async () => {
    if (streamRef.current) return true;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setMicError("mediadevices-unsupported");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      analyserRef.current = analyser;
      startAudioLoop();
      return true;
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        setMicError("not-allowed");
      } else {
        setMicError("failed");
      }
      return false;
    }
  }, [startAudioLoop]);

  const finalizeTranscript = useCallback((t) => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    const clean = (t || "").trim();
    try {
      stopSTTRef.current?.();
    } catch (_e) {
      /* ignore */
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (!clean) {
      setState("idle");
      return;
    }
    setState("thinking");
    cbRef.current.onFinalTranscript?.(clean);
  }, []);

  const _internalStartListening = useCallback(() => {
    if (!isSTTSupported()) {
      cbRef.current.onError?.("stt-unsupported");
      return;
    }
    try {
      stopSTTRef.current?.();
    } catch (_e) {
      /* ignore */
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setTranscript("");
    lastInterimRef.current = "";
    finalizedRef.current = false;
    setState("listening");

    stopSTTRef.current = startSTT({
      lang,
      continuous: false,
      onResult: ({ transcript: t, isFinal }) => {
        setTranscript(t);
        lastInterimRef.current = t;
        if (isFinal) {
          finalizeTranscript(t);
        } else {
          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = setTimeout(() => {
            finalizeTranscript(lastInterimRef.current);
          }, VAD_SILENCE_MS);
        }
      },
      onError: (code) => {
        if (code === "not-allowed" || code === "service-not-allowed") {
          setMicError("not-allowed");
        }
        cbRef.current.onError?.(code);
        setState((s) => (s === "listening" ? "idle" : s));
      },
      onEnd: () => {
        // If we got some interim but no final by now, VAD timer will handle it.
        // If nothing captured, fall back to idle.
        if (!finalizedRef.current) {
          setTimeout(() => {
            if (!finalizedRef.current && stateRef.current === "listening") {
              if (lastInterimRef.current.trim()) {
                finalizeTranscript(lastInterimRef.current);
              } else {
                setState("idle");
              }
            }
          }, 200);
        }
      },
    });
  }, [lang, finalizeTranscript]);

  useEffect(() => {
    startListeningRef.current = _internalStartListening;
  }, [_internalStartListening]);

  const startListening = useCallback(async () => {
    setMicError(null);
    // Unlock audio in the same user gesture that started this — critical on
    // iOS Safari where AudioContext + speechSynthesis both need a gesture.
    try {
      unlockAudio();
    } catch (_e) {
      /* ignore */
    }
    const ok = await initMic();
    if (!ok) {
      cbRef.current.onError?.("mic-failed");
      return;
    }
    _internalStartListening();
  }, [initMic, _internalStartListening]);

  const stopListening = useCallback(() => {
    try {
      stopSTTRef.current?.();
    } catch (_e) {
      /* ignore */
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setState("idle");
  }, []);

  const speak = useCallback(
    async (text, { autoListenAfter = false } = {}) => {
      if (!isTTSSupported() || !text) return;
      await initMic(); // ensure analyser is up for barge-in
      setState("speaking");
      try {
        window.speechSynthesis.cancel();
      } catch (_e) {
        /* ignore */
      }
      return new Promise((resolve) => {
        const utter = new SpeechSynthesisUtterance(String(text));
        const voice = pickSpanishVoice();
        if (voice) utter.voice = voice;
        utter.lang = voice?.lang || "es-MX";
        utter.rate = 1.0;
        utter.pitch = 1.05;
        utter.volume = 1.0;
        currentUtterRef.current = utter;
        const done = () => {
          if (currentUtterRef.current === utter) currentUtterRef.current = null;
          if (aliveRef.current && stateRef.current === "speaking") setState("idle");
          resolve();
          if (autoListenAfter && aliveRef.current && active) {
            // Auto-restart listening for continuous conversation loop
            setTimeout(() => {
              if (aliveRef.current && stateRef.current === "idle") {
                _internalStartListening();
              }
            }, 250);
          }
        };
        utter.onend = done;
        utter.onerror = done;
        try {
          window.speechSynthesis.speak(utter);
        } catch (_e) {
          done();
        }
      });
    },
    [initMic, active, _internalStartListening]
  );

  const setPipelineState = useCallback((s) => setState(s), []);

  // Cleanup on unmount OR when `active` becomes false
  useEffect(() => {
    if (active) return undefined;
    // teardown when overlay closes
    try {
      stopSTTRef.current?.();
    } catch (_e) {
      /* ignore */
    }
    stopSTTRef.current = null;
    try {
      window.speechSynthesis.cancel();
    } catch (_e) {
      /* ignore */
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch (_e) {
        /* ignore */
      }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setState("idle");
    setTranscript("");
    setAudioLevel(0);
    return undefined;
  }, [active]);

  useEffect(
    () => () => {
      aliveRef.current = false;
      try {
        stopSTTRef.current?.();
      } catch (_e) {
        /* ignore */
      }
      try {
        window.speechSynthesis.cancel();
      } catch (_e) {
        /* ignore */
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch (_e) {
          /* ignore */
        }
        audioCtxRef.current = null;
      }
      analyserRef.current = null;
    },
    []
  );

  return {
    state,
    transcript,
    audioLevel,
    analyserRef,
    supported,
    micError,
    startListening,
    stopListening,
    speak,
    cancelSpeaking,
    setPipelineState,
  };
}
